import React from "react";
import {observer} from "mobx-react";
import './modal.scss'
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import {InfoIcon} from "../../assets/svg-icons/InfoIcon";
import Utility from "../../Utility";
import {DATA_INTERVAL} from "../../MobxStores/DataStore";

const DuplicateItemsModal = observer(() => {
    const {modalStore, generalStore, dragStore, projectStore, procedureStore, dataStore} = useStores();

    const closeModal = () => {
        modalStore.isDuplicateItemsModalOpen = false;
        Utility.sleep(100).then(() => modalStore.resetModalData());
    }

    const renderBody = () =>
        <div>
            {generalStore.translate('duplicateAllSelectedItems')}? (
            {generalStore.translate('allFoldersAndProceduresInCurrentlySelectedFoldersWillAlsoBeDuplicated')})
        </div>

    const renderTitle = () => {
        return (
            <div className='attention-title'>
                <InfoIcon/>
                <div>{generalStore.translate('duplicateItems')}</div>
            </div>
        )
    }

    const duplicateFolders = async (projectId, projectFoldersMap, selectedFolders, updatedProcedures, updatedFolders) => {
        const duplicated = [0]; // array for thread safety
        const needToUpload = selectedFolders.length;
        await updateProgressLoader(duplicated[0], needToUpload, 'folders');

        const promises = selectedFolders.map(folderId => {
            const folder = projectFoldersMap.get(folderId);

            return projectStore.duplicateFolder(folder.importedFromWorkspace, folderId)
                .then(async ({
                                 newProcedures,
                                 newFolders
                             }) => {
                    newProcedures.forEach(newProcedure => updatedProcedures.set(newProcedure.ProcedureIdentifier, newProcedure));
                    newFolders.forEach(newFolder => updatedFolders.set(newFolder.FolderIdentifier, newFolder));
                    duplicated[0]++;
                    await updateProgressLoader(duplicated[0], needToUpload, 'folders');
                });
        });
        await Promise.all(promises);
        await updateProgressLoader(0, 0, 'folders', true);
    }

    const duplicateProcedures = async (projectId, projectProceduresMap, selectedProcedures, updatedProcedures) => {
        const duplicated = [0]; // array for thread safety
        const needToUpload = selectedProcedures.length;
        await updateProgressLoader(duplicated[0], needToUpload, 'procedures');

        const promises = selectedProcedures.map(procedureId => {
            const procedure = projectProceduresMap.get(procedureId);

            return procedureStore.duplicateProcedure(procedure.importedFromWorkspace, procedureId, projectId)
                .then(async newProcedure => {
                    updatedProcedures.set(newProcedure.ProcedureIdentifier, newProcedure);
                    duplicated[0]++;
                    await updateProgressLoader(duplicated[0], needToUpload, 'procedures');
                });
        });
        await Promise.all(promises);
        await updateProgressLoader(0, 0, 'procedures', true);
    }

    const updateProgressLoader = async (uploaded, needToUpload, itemsName, shouldClose = false) => {
        if (shouldClose) {
            generalStore.progressLoaderValue = null;
            return;
        }
        generalStore.progressLoaderValue = 100 * (uploaded / needToUpload);
        generalStore.progressLoaderString = `${generalStore.translate('duplicating')}: ${uploaded} ${generalStore.translate('of', true)} ${needToUpload} ${generalStore.translate(itemsName, true)}`;
        await Utility.sleep(200); // making it visible to the human eye
    }

    const onConfirm = async () => {
        const selectedFoldersSet = dragStore.selectedFoldersIds;
        const selectedProceduresSet = dragStore.selectedProceduresIds;

        const updatedProcedures = new Map();
        const updatedFolders = new Map();

        const projectId = projectStore.currentProject?.ProjectIdentifier;
        const projectProceduresMap = dataStore.projectsProceduresMap.get(projectId);
        const projectFoldersMap = dataStore.projectsFoldersMap.get(projectId);

        if (!!selectedFoldersSet.size) {
            await duplicateFolders(projectId, projectFoldersMap, [...selectedFoldersSet.values()], updatedProcedures, updatedFolders);
        }

        if (!!selectedProceduresSet.size) {
            await duplicateProcedures(projectId, projectProceduresMap, [...selectedProceduresSet.values()], updatedProcedures);
        }

        generalStore.isLoaderOpen = true;

        updatedFolders.forEach(folder => projectFoldersMap.set(folder.FolderIdentifier, folder));
        updatedProcedures.forEach(procedure => projectProceduresMap.set(procedure.ProcedureIdentifier, procedure));

        dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
        dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
        dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);

        await projectStore.refreshProjectDetails();

        modalStore.modalData.updateParent();
        generalStore.isLoaderOpen = false;
        closeModal();
    }

    return (
        <GeneralDialog
            openControl='isDuplicateItemsModalOpen'
            className='attention-dialog'
            onEnter={null}
            title={renderTitle()}
            body={renderBody()}
            isConfirmDisabled={false}
            onConfirm={onConfirm}
            confirmText={generalStore.translate('confirm')}
            buttonClass='notification'
            specialOnClose={closeModal}
        />
    );
});

export default DuplicateItemsModal;
